import { useCallback, useEffect, useRef, useState, type ClipboardEvent, type DragEvent } from "react";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { type ImageAttachment } from "@/components/ai/prompt-input/state";

const ALLOWED_TYPES = ["image/png", "image/jpeg"];
const MAX_SIZE_BYTES = 4 * 1024 * 1024; // 4MB

type ToastFn = (props: { title: string; description?: string }) => void;

/**
 * Process files and convert them to ImageAttachment objects
 */
export async function processImageFiles(files: FileList | File[] | null, toast: ToastFn): Promise<ImageAttachment[]> {
    if (!files) return [];

    const newImages: ImageAttachment[] = [];

    for (const file of Array.from(files)) {
        if (!ALLOWED_TYPES.includes(file.type)) {
            toast({
                title: "Unsupported file type",
                description: `${file.name}: Only PNG and JPEG are supported`,
            });
            continue;
        }

        if (file.size > MAX_SIZE_BYTES) {
            toast({
                title: "File too large",
                description: `${file.name}: Maximum size is 4MB`,
            });
            continue;
        }

        try {
            const buffer = await file.arrayBuffer();
            const preview = URL.createObjectURL(file);

            newImages.push({
                id: crypto.randomUUID(),
                data: new Uint8Array(buffer),
                preview,
                name: file.name,
                mimeType: file.type,
            });
        } catch {
            toast({
                title: "Failed to process image",
                description: `${file.name}: Could not read file`,
            });
        }
    }

    return newImages;
}

/**
 * Extract image files from clipboard data
 */
function getImageFilesFromClipboard(clipboardData: DataTransfer | null): File[] {
    if (!clipboardData) return [];

    const files: File[] = [];
    for (const item of Array.from(clipboardData.items)) {
        if (item.kind === "file" && ALLOWED_TYPES.includes(item.type)) {
            const file = item.getAsFile();
            if (file) {
                files.push(file);
            }
        }
    }
    return files;
}

/**
 * Hook for handling image drag-and-drop on a container element
 */
export function useImageDrop(onImagesSelected: (images: ImageAttachment[]) => void, disabled?: boolean) {
    const { toast } = useToast();
    const [isDragging, setIsDragging] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const dragCounterRef = useRef(0);

    const handleDragEnter = useCallback(
        (e: DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            if (disabled) return;

            dragCounterRef.current++;
            if (e.dataTransfer?.types.includes("Files")) {
                setIsDragging(true);
            }
        },
        [disabled],
    );

    const handleDragLeave = useCallback(
        (e: DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            if (disabled) return;

            dragCounterRef.current--;
            if (dragCounterRef.current === 0) {
                setIsDragging(false);
            }
        },
        [disabled],
    );

    const handleDragOver = useCallback((e: DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleDrop = useCallback(
        async (e: DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            dragCounterRef.current = 0;
            setIsDragging(false);

            if (disabled) return;

            const files = e.dataTransfer?.files;
            if (files && files.length > 0) {
                setIsProcessing(true);
                try {
                    const images = await processImageFiles(files, toast);
                    if (images.length > 0) {
                        onImagesSelected(images);
                    }
                } finally {
                    setIsProcessing(false);
                }
            }
        },
        [disabled, onImagesSelected, toast],
    );

    return {
        isDragging,
        isProcessing,
        dragHandlers: {
            onDragEnter: handleDragEnter,
            onDragLeave: handleDragLeave,
            onDragOver: handleDragOver,
            onDrop: handleDrop,
        },
    };
}

/**
 * Hook for handling image paste from clipboard.
 * Returns a paste handler to attach to an element's onPaste event.
 */
export function useImagePaste(onImagesSelected: (images: ImageAttachment[]) => void, disabled?: boolean) {
    const { toast } = useToast();
    const [isProcessing, setIsProcessing] = useState(false);

    const handlePaste = useCallback(
        async (e: ClipboardEvent) => {
            if (disabled) return;

            const imageFiles = getImageFilesFromClipboard(e.clipboardData);
            if (imageFiles.length === 0) return;

            // Prevent default paste behavior for images
            e.preventDefault();

            setIsProcessing(true);
            try {
                const images = await processImageFiles(imageFiles, toast);
                if (images.length > 0) {
                    onImagesSelected(images);
                }
            } finally {
                setIsProcessing(false);
            }
        },
        [disabled, onImagesSelected, toast],
    );

    return { handlePaste, isProcessing };
}

/**
 * Hook that attaches a global paste listener when an element is focused.
 * Useful for handling paste when the focused element doesn't directly receive paste events.
 */
export function useGlobalImagePaste(
    elementRef: React.RefObject<HTMLElement | null>,
    onImagesSelected: (images: ImageAttachment[]) => void,
    disabled?: boolean,
) {
    const { toast } = useToast();

    useEffect(() => {
        if (disabled) return;

        const handleGlobalPaste = async (e: globalThis.ClipboardEvent) => {
            // Only handle paste if our element or a child is focused
            const activeElement = document.activeElement;
            if (!elementRef.current?.contains(activeElement)) return;

            const imageFiles = getImageFilesFromClipboard(e.clipboardData);
            if (imageFiles.length === 0) return;

            // Prevent default paste behavior for images
            e.preventDefault();

            const images = await processImageFiles(imageFiles, toast);
            if (images.length > 0) {
                onImagesSelected(images);
            }
        };

        document.addEventListener("paste", handleGlobalPaste);
        return () => document.removeEventListener("paste", handleGlobalPaste);
    }, [disabled, elementRef, onImagesSelected, toast]);
}
